import React, { useState } from "react";
import ReactDOM from "react-dom";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import Icon from "components/icon";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
  testId?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  testId?: string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ items, testId }) => {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-end",
    strategy: "fixed",
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <div className="relative">
      <button
        ref={refs.setReference}
        type="button"
        {...getReferenceProps({
          onClick: (event: React.MouseEvent) => event.stopPropagation(),
        })}
        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-greyScale-90 transition-colors cursor-pointer"
        data-testid={testId || "btn-action-menu"}
      >
        <Icon name="more" size={16} />
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={refs.setFloating}
          className="fixed z-dropdown min-w-[160px] rounded-xl border border-border bg-white py-1 shadow-lg"
          style={{
            ...floatingStyles,
            visibility: isPositioned ? "visible" : "hidden",
          }}
          {...getFloatingProps()}
        >
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                item.disabled 
                  ? "opacity-50 cursor-not-allowed" 
                  : "hover:bg-greyScale-90 cursor-pointer"
              } ${
                item.variant === "danger" && !item.disabled
                  ? "text-error-500"
                  : "text-text-primary"
              }`}
              data-testid={item.testId || `action-menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionMenu;
